import React, { useState, useRef, useEffect, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useMediaQuery } from '@material-ui/core';
import { createTheme, makeStyles, useTheme } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';
import { useAuthState, Loading, useRedirect, useNotify } from 'react-admin';
import * as Auth from '../../api';
import ForgotPasswordForm from './form';
import defaultTheme from '../login/defaultTheme';
import Notification from '../login/notification';

const useStyles = makeStyles(
  (theme) => ({
    main: {
      display: 'flex',
      flexDirection: 'row',
      minHeight: '100vh',
      height: '100vh',
      width: '100vw',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
    },
    leftSection: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
    formContainer: {
      width: '100%',
      maxWidth: '448px',
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
      marginBottom: theme.spacing(1.5),
      color: theme.palette.text.primary,
      [theme.breakpoints.down('sm')]: {
        fontSize: '2rem',
      },
    },
    description: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(3),
      fontSize: '1rem',
    },
    rightSection: {
      flex: 1,
      position: 'relative',
      padding: theme.spacing(2),
      display: 'none',
      [theme.breakpoints.up('md')]: {
        display: 'block',
      },
    },
    heroImage: {
      position: 'absolute',
      inset: theme.spacing(1),
      borderRadius: '24px',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    },
  }),
  { name: 'ForgotPasswordPage' }
);

const ForgotPasswordContent = ({ classes, heroImageSrc, heroImageRef, onSubmit, submitErrors }) => {
  const defaultTitle = (
    <span style={{ fontWeight: 300, letterSpacing: '-0.02em' }}>
      Recover password
    </span>
  );

  const defaultDescription = 'We will send a confirmation email to this address:';

  return (
    <div className={classes.main}>
      <section className={classes.leftSection}>
        <div className={classes.formContainer}>
          <h1 className={classes.title}>
            {defaultTitle}
          </h1>
          <p className={classes.description}>
            {defaultDescription}
          </p>
          <ForgotPasswordForm onSubmit={onSubmit} submitErrors={submitErrors} />
        </div>
      </section>
      {heroImageSrc && (
        <section className={classes.rightSection}>
          <div className={classes.heroImage} ref={heroImageRef} />
        </section>
      )}
      <Notification />
    </div>
  );
};

ForgotPasswordContent.propTypes = {
  classes: PropTypes.object.isRequired,
  heroImageSrc: PropTypes.string,
  heroImageRef: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitErrors: PropTypes.array,
};

const ForgotPassword = () => {
  const classes = useStyles();
  const { loading, authenticated } = useAuthState();
  const redirect = useRedirect();
  const notify = useNotify();
  const heroImageRef = useRef();
  const muiTheme = useMemo(() => createTheme(defaultTheme), []);

  const [submitErrors, setSubmitErrors] = useState([]);
  const [requesting, setRequesting] = useState(false);

  const heroImageSrc = "https://external-preview.redd.it/_hBu27b4O1p5_vyMUzseK_QmThFmJE80sbDkgLNfJYI.jpg?auto=webp&s=31696bcde387e98bdfa52668a94c9a6865f5fa25";

  // Load background image asynchronously
  useEffect(() => {
    if (heroImageSrc && heroImageRef.current) {
      const img = new Image();
      img.onload = () => {
        if (heroImageRef.current) {
          heroImageRef.current.style.backgroundImage = `url(${heroImageSrc})`;
        }
      };
      img.src = heroImageSrc;
    }
  }, [heroImageSrc]);

  const onFormSubmit = async (data) => {
    setRequesting(true);
    try {
      await Auth.forgotPassword(data);
      notify('Email sent. Please check your email');
      redirect('/login');
    } catch (e) {
      const { errors } = e.response?.data || { errors: [] };
      setSubmitErrors(errors);
    }
    setRequesting(false);
  };

  if (loading || requesting) {
    return <Loading loadingPrimary="Processing your request. Please wait..." loadingSecondary=""/>;
  }
  if (authenticated) {
    redirect('/');
    return null;
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <ForgotPasswordContent
        classes={classes}
        heroImageSrc={heroImageSrc}
        heroImageRef={heroImageRef}
        onSubmit={onFormSubmit}
        submitErrors={submitErrors}
      />
    </ThemeProvider>
  );
};

export default withRouter(ForgotPassword);
